import { FC, HTMLAttributes } from "react";
import { useSelector } from "react-redux";
import * as _ from "lodash";

import { useTranslation, Enterprise as Content } from "@ctra/i18n";
import {
  PenEntity,
  Enterprise,
  FarmEntity,
  EnterpriseAppState,
  PensList,
  HerdGroupList,
  HerdGroupEntity
} from "@ctra/api";
import { Charts } from "@ctra/components";
import { useGoogleAnalytics } from "@ctra/analytics";

import { GAActions } from "../../analytics";

const { MultiDropdown } = Charts;

interface PenFilterProps {
  /**
   * Allow multiple selections
   */
  multiple?: boolean;
  /**
   * selected herd group(s)
   */
  selection: Array<PenEntity["id"]>;
  /**
   * handle herd group change
   */
  onPenChange: (pens: Array<PenEntity["id"]>) => void;
  /**
   * list of farm ids to pre-filter the herd groups against
   */
  farmIDList?: Array<FarmEntity["id"]>;
  /**
   * disabled state
   */
  disabled?: boolean;
}

/**
 * Herd group filtering
 * @param {boolean | undefined} multiple
 * @param {Array<PenEntity["id"]>} selection
 * @param {Array<FarmEntity["id"]> | undefined} farmIDList
 * @param {(pens: Array<PenEntity["id"]>) => void} onPenChange
 * @param {boolean | undefined} disabled
 * @param {Pick<PenFilterProps & Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> & {children?: React.ReactNode | undefined}, "children" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "className" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture">} rest
 * @return {JSX.Element}
 * @constructor
 */
const PenFilter: FC<PenFilterProps & Omit<HTMLAttributes<HTMLDivElement>, "onChange">> = ({
  multiple = false,
  selection,
  farmIDList = [],
  onPenChange,
  disabled,
  ...rest
}) => {
  const { t } = useTranslation();
  const { trackEvent, label } = useGoogleAnalytics<{ label: string }>();

  /**
   * Pick the pens which are available for the given farms
   * @type {PensList}
   */
  const pens = useSelector<EnterpriseAppState, PensList>((state) =>
    Enterprise.entities.getPens(state, { farmID: farmIDList })
  );

  /**
   * Pick the herd groups as well
   * @type {HerdGroupList}
   */
  const herdGroups = useSelector<EnterpriseAppState, HerdGroupList>((state) =>
    Enterprise.entities.getHerdGroups(state, { farmID: farmIDList })
  );

  /**
   * Combine the two lists
   * @type {{[p: string]: PenEntity, [p: number]: HerdGroupEntity}}
   */
  const input: Record<HerdGroupEntity["id"] | PenEntity["id"], HerdGroupEntity | PenEntity> = {
    ...pens,
    ...herdGroups
  };

  return (
    <MultiDropdown<PenEntity["id"]>
      disabled={disabled}
      limit={multiple ? null : 1}
      label={t<string>(Content.chart.filters.pen.label)}
      values={_.uniq(
        _.map(
          _.sortBy(input, (entity) => parseFloat(entity.name)),
          "name"
        )
      )}
      selection={selection}
      onChange={(values) => {
        /* istanbul ignore next */
        trackEvent(GAActions.selectPen, { label: `${label} - ${values.join(",")}` });
        onPenChange(values as Array<PenEntity["id"]>);
      }}
      filterOption={(input, option) => {
        return _.includes(_.toLower(option?.children), _.toLower(input));
      }}
      getLabel={(penName) => t<string>(Content.chart.filters.pen.single, { name: penName })}
      placeholder={t<string>(Content.chart.filters.pen.placeholder)}
      {...rest}
    />
  );
};

export default PenFilter;
